import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import ArrowLeftIcon from '@components/icons/ArrowLeft.svg';
import ArrowRightIcon from '@components/icons/ArrowRight.svg';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { TProps } from './types/TProps';

export const TranscriptSearchBox = ({
  namePrefix,
  query,
  matchCount,
  currentMatchNumber,
  caseSensitive,
  wholeWord,
  focusSignal,
  focusSignalEnabled = true,
  className,
  onQueryChange,
  onCaseSensitiveChange,
  onWholeWordChange,
  onPrevious,
  onNext,
  onActivate,
}: TProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastSeenFocusSignalRef = useRef(focusSignal);

  useEffect(() => {
    if (focusSignal === lastSeenFocusSignalRef.current) {
      return;
    }

    lastSeenFocusSignalRef.current = focusSignal;

    if (!focusSignalEnabled) {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [focusSignal, focusSignalEnabled]);

  const handleKeyDown = (keyboardEvent: KeyboardEvent<HTMLInputElement>) => {
    if (keyboardEvent.key === 'Enter') {
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();

      if (keyboardEvent.shiftKey) {
        onPrevious();
      } else {
        onNext();
      }

      return;
    }

    if (keyboardEvent.key === 'Escape') {
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      onQueryChange('');
      keyboardEvent.currentTarget.blur();
    }
  };

  const counterText = matchCount > 0 ? `${currentMatchNumber} / ${matchCount}` : query ? 'No results' : '';

  return (
    <div
      className={[
        'flex flex-nowrap items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 text-sm text-[var(--color-text)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onFocusCapture={onActivate}
    >
      <input
        ref={inputRef}
        type="search"
        className="form-control h-11 min-w-0 flex-1 px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
        value={query}
        onChange={(changeEvent) => onQueryChange(changeEvent.target.value)}
        onKeyDown={handleKeyDown}
        aria-label="Search transcript"
        placeholder="Search transcript"
      />
      <span
        className="min-w-16 shrink-0 whitespace-nowrap text-center text-xs text-[var(--color-text-muted)]"
        aria-live="polite"
        aria-atomic="true"
      >
        {counterText}
      </span>
      <IconButtonClean
        type="button"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
        disabled={matchCount === 0}
        onClick={onPrevious}
        aria-label="Previous match"
      >
        <ArrowLeftIcon className="h-3 w-3" aria-hidden="true" focusable="false" />
      </IconButtonClean>
      <IconButtonClean
        type="button"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
        disabled={matchCount === 0}
        onClick={onNext}
        aria-label="Next match"
      >
        <ArrowRightIcon className="h-3 w-3" aria-hidden="true" focusable="false" />
      </IconButtonClean>
      <CheckBox
        name={`${namePrefix}-transcript-search-case-sensitive`}
        className="min-h-11 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
        label="Match case"
        checked={caseSensitive}
        onChange={onCaseSensitiveChange}
      />
      <CheckBox
        name={`${namePrefix}-transcript-search-whole-word`}
        className="min-h-11 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-link)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]"
        label="Whole word"
        checked={wholeWord}
        onChange={onWholeWordChange}
      />
    </div>
  );
};
